import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { setLoading, signUpSuccess } from '../../features/authSlice';
import CarouselComponent from '../../common/components/CarouselComponent';
import SignUpForm from './SignUpForm';
import CommonButton from '../../common/components/CommonButton';
import CommonDialog from '../../common/components/CommonDialog';
import OTPInput from '../../common/components/OtpInput';
import { register, resendOtp, userVerify } from '../../api/authApi';
import { Close } from '@mui/icons-material';
import { LoginType, UserTypeId, VerificationMethod } from '../../common/const';
import { showNotification } from '../../common/components/Notification/ToastifyNotification';
import CommonCard from '../../common/components/CommonCard';


const SignUp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { loading, user } = useSelector((state) => state.auth);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [otp, setOtp] = useState('');
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    birthdate: '',
    address: '',
    state: '',
    city: '',
    zipcode: '',
    password: '',
    confirmpassword: '',
    acceptTerms: true,
    usertype: UserTypeId.Student
  });

  useEffect(() => {
    if (countdown > 0 && dialogOpen) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown, dialogOpen]);

  const handleSubmit = async (values) => {
    try {
      dispatch(setLoading({
        loading: true
      }));
      const response = await register({
        first_name: values.firstname,
        last_name: values.lastname,
        email: values.email,
        phone_number: formData.phone,
        date_of_birth: new Date(values.birthdate).toISOString(),
        address: values.address,
        city: values.city,
        state: values.state,
        zip_code: values.zipcode,
        password: values.password,
        user_type_id: values.usertype,
        login_type: LoginType.EMAIL
      });
      dispatch(signUpSuccess({
        user: response.data,
      }));
      showNotification('User registration successful!', 'success');
      setDialogOpen(true);
    } catch (err) {
      const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err.response.data?.error;
      dispatch(setLoading({
        loading: false
      }));
      showNotification(errorMessage, 'error');
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <CommonDialog
        open={dialogOpen}
        size="xs"
        title={
          <Close
            sx={{
              color: theme.palette.background.dark, fontSize: 20,
              float: 'right',
              cursor: 'pointer'
            }}
            onClick={() => {
              setDialogOpen(false);
            }}
          />
        }
        content={(
          <Box sx={{ textAlign: 'center', px: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '24px'
              }}
            >
              Verification
            </Typography>

            {/* Instruction */}
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.background.dark,
                fontSize: '14px',
                lineHeight: 1.5
              }}
            >
              Enter the 6-digit code sent to you at
            </Typography>

            {/* Phone Number */}
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              {`+${formData.phone}`}
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              OR
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              {formData.email}
            </Typography>

            <OTPInput
              length={6}
              onChange={(otp) => setOtp(otp)}
            />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <CommonButton
                label="Verify"
                marginBottom={2}
                disabled={otp.length < 6}
                handleClick={async () => {
                  dispatch(setLoading({
                    loading: true
                  }));
                  const response = await userVerify({
                    otp_sent_to: formData.phone,
                    otp: otp,
                    verification_method: VerificationMethod.mobile,
                    user_id: user.user_id
                  });
                  if (response.data.token) {
                    dispatch(setLoading({
                      loading: false
                    }));
                    setFormData({
                      firstname: '',
                      lastname: '',
                      email: '',
                      phone: '',
                      birthdate: '',
                      address: '',
                      state: '',
                      city: '',
                      zipcode: '',
                      password: '',
                      confirmpassword: '',
                      acceptTerms: true,
                      usertype: UserTypeId.Student
                    });
                    localStorage.setItem('temp-token', response.data.token);
                    navigate(`/profile-setup`);
                  }
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 0,
                  mb: 2
                }}>
                <Link
                  sx={{
                    color: countdown > 0 ? theme.palette.text.disabled : theme.palette.secondary.main,
                    textDecoration: 'none',
                    cursor: countdown > 0 ? 'default' : 'pointer',
                    pointerEvents: countdown > 0 ? 'none' : 'auto', // Prevents all interactions
                    opacity: countdown > 0 ? 0.5 : 1,
                    fontSize: '14px'
                  }}
                  onClick={countdown > 0 ? undefined : async () => {
                    dispatch(setLoading({
                      loading: true
                    }));
                    const response = await resendOtp({
                      otp_sent_to: formData.phone,
                      verification_method: VerificationMethod.mobile,
                      user_id: user.user_id
                    });
                    if (response.success) {
                      showNotification(response.message, 'success');
                      setCountdown(60);
                    }
                  }}
                >
                  Resend OTP
                </Link>
                <Typography
                  color="primary.main"
                >
                  (in {countdown}s)
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      />
      <Paper
        elevation={isMobile ? 0 : 3}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 0,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', minHeight: '100vh', overflow: 'hidden' }}>
          {/* Left Side - Carousel */}
          <Box
            sx={{
              width: '54%',
              display: { xs: 'none', md: 'block' },
              flexShrink: 0,
            }}
          >
            <CarouselComponent />
          </Box>

          {/* Right Side - Form */}
          <Box
            sx={{
              height: '100vh',
              width: '500px',
              margin: '0 auto',
              overflowY: 'auto',
              pt: { lg: 3 }
            }}
          >
            <SignUpForm
              onSubmit={handleSubmit}
              loading={loading}
              formData={formData}
              setFormData={setFormData}
            />
          </Box>
        </Box>

        {isMobile && (
          <Box sx={{ height: '100%', overflowY: 'auto' }}>
            <SignUpForm
              onSubmit={handleSubmit}
              loading={loading}
              formData={formData}
              setFormData={setFormData}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SignUp;